import { createSlice } from "@reduxjs/toolkit";
import {
    getAllBillingsByCaseIdAction,
    getBillingByIdAction,
    createBillingAction,
    updateBillingAction,
    updateAgreedFeesAction,
    updateInitialBillAction,
    updateFinalBillAction,
    updateReconciliationAction,
    deleteBillingAction
} from "../../actions/Case/caseBillingsAction";

const initialState = {
    billings: [],
    selectedBilling: null,
    loading: false,
    error: null,
    success: false,
    message: ""
};

const billingsSlice = createSlice({
    name: "billings",
    initialState,
    reducers: {
        resetBillingsState: (state) => {
            return initialState;
        },
        clearBillingsError: (state) => {
            state.error = null;
            state.message = "";
        },
        clearSelectedBilling: (state) => {
            state.selectedBilling = null;
        }
    },
    extraReducers: (builder) => {
        // =========== GET ALL BILLINGS BY CASE ID ===========
        builder
            .addCase(getAllBillingsByCaseIdAction.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(getAllBillingsByCaseIdAction.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.billings = action.payload.billings;
                state.message = action.payload.message;
            })
            .addCase(getAllBillingsByCaseIdAction.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Failed to fetch billings";
                state.billings = [];
            })

        // =========== GET SINGLE BILLING BY ID ===========
        builder
            .addCase(getBillingByIdAction.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(getBillingByIdAction.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.selectedBilling = action.payload.billing;
                state.message = action.payload.message;
            })
            .addCase(getBillingByIdAction.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Failed to fetch billing";
                state.selectedBilling = null;
            })

        // =========== CREATE NEW BILLING ===========
        builder
            .addCase(createBillingAction.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(createBillingAction.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.billings.unshift(action.payload.billing);
                state.message = action.payload.message;
            })
            .addCase(createBillingAction.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Failed to create billing";
            })

        // =========== UPDATE BILLING ===========
        builder
            .addCase(updateBillingAction.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(updateBillingAction.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;

                const index = state.billings.findIndex(
                    b => b.id === action.payload.billingID
                );
                if (index !== -1) {
                    state.billings[index] = action.payload.billing;
                }

                if (state.selectedBilling?.id === action.payload.billingID) {
                    state.selectedBilling = action.payload.billing;
                }
                state.message = action.payload.message;
            })
            .addCase(updateBillingAction.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Failed to update billing";
            })

        // =========== DELETE BILLING ===========
        builder
            .addCase(deleteBillingAction.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(deleteBillingAction.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;

                state.billings = state.billings.filter(
                    b => b.id !== action.payload.billingID
                );

                if (state.selectedBilling?.id === action.payload.billingID) {
                    state.selectedBilling = null;
                }
                state.message = action.payload.message;
            })
            .addCase(deleteBillingAction.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Failed to delete billing";
            })

        // =========== UPDATE AGREED FEES ===========
        builder
            .addCase(updateAgreedFeesAction.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(updateAgreedFeesAction.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;

                const index = state.billings.findIndex(
                    b => b.id === action.payload.billingID
                );
                if (index !== -1) {
                    state.billings[index] = action.payload.billing;
                }

                if (state.selectedBilling?.id === action.payload.billingID) {
                    state.selectedBilling = action.payload.billing;
                }
                state.message = action.payload.message;
            })
            .addCase(updateAgreedFeesAction.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Failed to update agreed fees";
            })

        // =========== UPDATE INITIAL BILL ===========
        builder
            .addCase(updateInitialBillAction.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(updateInitialBillAction.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;

                const index = state.billings.findIndex(
                    b => b.id === action.payload.billingID
                );
                if (index !== -1) {
                    state.billings[index] = action.payload.billing;
                }

                if (state.selectedBilling?.id === action.payload.billingID) {
                    state.selectedBilling = action.payload.billing;
                }
                state.message = action.payload.message;
            })
            .addCase(updateInitialBillAction.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Failed to update initial bill";
            })

        // =========== UPDATE FINAL BILL ===========
        builder
            .addCase(updateFinalBillAction.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(updateFinalBillAction.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;

                const index = state.billings.findIndex(
                    b => b.id === action.payload.billingID
                );
                if (index !== -1) {
                    state.billings[index] = action.payload.billing;
                }

                if (state.selectedBilling?.id === action.payload.billingID) {
                    state.selectedBilling = action.payload.billing;
                }
                state.message = action.payload.message;
            })
            .addCase(updateFinalBillAction.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Failed to update final bill";
            })

        // =========== UPDATE RECONCILIATION ===========
        builder
            .addCase(updateReconciliationAction.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(updateReconciliationAction.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;

                const index = state.billings.findIndex(
                    b => b.id === action.payload.billingID
                );
                if (index !== -1) {
                    state.billings[index] = action.payload.billing;
                }

                if (state.selectedBilling?.id === action.payload.billingID) {
                    state.selectedBilling = action.payload.billing;
                }
                state.message = action.payload.message;
            })
            .addCase(updateReconciliationAction.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Failed to update reconciliation";
            })
    }
});

export const {
    resetBillingsState,
    clearBillingsError,
    clearSelectedBilling
} = billingsSlice.actions;

export default billingsSlice.reducer;
